import {
  Button,
  Chip,
  ConfirmSheet,
  DrinkIcon,
  DrinkTicketCard,
  formatBaht,
  Icons,
  WaxSeal,
} from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { track } from "../features/analytics/track.ts";
import {
  CUSTOMER_DEMO_VENUE_ID,
  customerBackendApi,
  menuItemFromDto,
} from "../features/api/backend.ts";
import { useT } from "../features/i18n/index.ts";
import { useSession } from "../features/session/session-context.tsx";
import { getMode } from "../features/tonight/mode.ts";
import { giftItems, giftMessages, mockTables } from "../mocks/data.ts";

export const Route = createFileRoute("/send-drink")({
  validateSearch: (search: Record<string, unknown>): { to?: string } => ({
    to: typeof search.to === "string" ? search.to : undefined,
  }),
  component: SendDrinkScreen,
});

type Phase = "compose" | "sent";

function SendDrinkScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { to } = Route.useSearch();
  const { mode, tableCode, settings, sendGiftRequest, canStartSocial, sentSocial, toast } =
    useSession();

  const backendEnabled = customerBackendApi !== null;
  const aroundQuery = useQuery({
    queryKey: ["customer", "around"],
    queryFn: () =>
      customerBackendApi?.listAroundTables() ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
    refetchInterval: 10_000,
  });
  const menuQuery = useQuery({
    queryKey: ["customer", "menu", CUSTOMER_DEMO_VENUE_ID, "giftable"],
    queryFn: () =>
      customerBackendApi?.getMenu(CUSTOMER_DEMO_VENUE_ID) ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
  });
  // Targets come from the consent-filtered Around feed so we always have a real
  // recipient party id for the gift (mock mode keeps the static mock tables).
  const targets = useMemo(
    () =>
      backendEnabled
        ? (aroundQuery.data ?? [])
            .filter((t) => !t.isOwnTable)
            .map((t) => ({ code: t.code, partyId: t.partyId, acceptingGifts: t.acceptingGifts }))
        : mockTables.map((t) => ({ code: t.code, partyId: undefined, acceptingGifts: true })),
    [backendEnabled, aroundQuery.data],
  );
  const giftOptions = useMemo(
    () =>
      backendEnabled
        ? (menuQuery.data?.items
            .filter((item) => item.giftable)
            .map(menuItemFromDto)
            .slice(0, 6) ?? [])
        : giftItems,
    [backendEnabled, menuQuery.data],
  );
  const target = targets.find((t) => t.code === to) ?? targets[0];
  const [drinkId, setDrinkId] = useState(giftOptions[0]?.id ?? "");
  const [message, setMessage] = useState(giftMessages[0] ?? "");
  const [contact, setContact] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("compose");

  useEffect(() => {
    if (giftOptions.length > 0 && !giftOptions.some((item) => item.id === drinkId)) {
      setDrinkId(giftOptions[0]?.id ?? "");
    }
  }, [drinkId, giftOptions]);

  // AEV-015 gift_started: composer opened toward a resolved target table.
  // ASSUMPTION: `target_table_ref`/`source_table_ref` use the best available
  // client-side table reference (backend `partyId` when known, else the
  // human-readable table code in mock mode) — the client has no dedicated
  // own-party-ref in session state today.
  const targetRef = target?.partyId ?? target?.code;
  useEffect(() => {
    if (!targetRef) return;
    track("gift_started", { target_table_ref: targetRef, source_table_ref: tableCode });
  }, [targetRef, tableCode]);

  const drink = giftOptions.find((d) => d.id === drinkId) ?? giftOptions[0];
  const modeMeta = getMode(mode);
  const latestSent = sentSocial.find(
    (request) => request.kind === "gift" && request.toTable === target?.code,
  );
  const guard = useMemo(
    () =>
      target
        ? canStartSocial({ toTable: target.code, kind: "gift", requestedContact: contact })
        : { ok: false, reason: t("send.noTarget") },
    [canStartSocial, contact, target, t],
  );

  const send = () => {
    if (!drink || !target) return;
    const result = sendGiftRequest({
      toTable: target.code,
      subject: drink.nameTh ?? drink.name,
      message,
      requestedContact: contact,
      menuItemId: drink.id,
      targetPartyId: target.partyId,
    });
    setConfirmOpen(false);
    if (!result.ok) {
      toast(result.reason ?? t("common.cantSendYet"));
      return;
    }
    setPhase("sent");
    toast(t("send.sealedToast", { table: target.code }));
  };

  if (phase === "sent" && drink && target) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-6 px-[18px] py-8">
        <WaxSeal size={84} tone="lime" stamped />
        <div className="w-full">
          <DrinkTicketCard
            animated
            drinkName={drink.name}
            drinkNameTh={drink.nameTh}
            iconName={drink.iconName}
            price={formatBaht(drink.priceMinor)}
            fromLabel={t("send.fromYou", { table: tableCode })}
            toLabel={t("common.table", { code: target.code })}
            message={message || undefined}
            status="pending"
          />
        </div>
        <div className="glass w-full rounded-xl p-4 text-center">
          <p className="text-cream-2 text-sm">{t("send.sentNote")}</p>
          <p className="mt-1 text-cream-3 text-xs">
            {t("send.mockStatus", { status: latestSent?.status ?? "pending_acceptance" })}
            {contact ? ` · ${t("send.igAttachedShort")}` : ""}
          </p>
        </div>
        <Button className="w-full" onClick={() => navigate({ to: "/home" })}>
          {t("common.backHome")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/around" })}
      >
        <Icons.IconBack size={18} /> {t("common.back")}
      </button>

      <div className="jod-kicker">{t("send.kicker")}</div>
      <h1 className="h-display mt-1 text-[1.7rem]">Just One Drink</h1>
      <p className="mt-1 text-cream-3 text-sm">
        {t("common.toTable", { table: target?.code ?? t("send.selectedFallback") })}
      </p>

      <div className="glass mt-4 flex items-start gap-3 rounded-lg px-4 py-3 text-cream-2 text-sm">
        <span className="text-gold">
          {mode === "private" ? <Icons.IconLock size={17} /> : <Icons.IconEye size={17} />}
        </span>
        <div>
          <div>{t("send.currentMode", { mode: modeMeta.label })}</div>
          <div className="text-cream-3 text-xs">{t("send.modeGateNote")}</div>
        </div>
      </div>

      {drink && target ? (
        <div className="mt-5">
          <DrinkTicketCard
            drinkName={drink.name}
            drinkNameTh={drink.nameTh}
            iconName={drink.iconName}
            price={formatBaht(drink.priceMinor)}
            fromLabel={t("send.fromYou", { table: tableCode })}
            toLabel={t("common.table", { code: target.code })}
            message={message || undefined}
            status="pending"
          />
        </div>
      ) : null}

      <h2 className="jod-kicker mt-6 mb-2.5">{t("send.chooseItem")}</h2>
      {backendEnabled && menuQuery.isLoading ? (
        <p className="mb-2 rounded-lg border border-hairline bg-glass px-3 py-2 text-cream-3 text-xs">
          {t("send.loadingGifts")}
        </p>
      ) : null}
      {backendEnabled && (menuQuery.isError || aroundQuery.isError) ? (
        <p className="mb-2 rounded-lg bg-red-500/10 px-3 py-2 text-red-100 text-xs">
          {t("send.loadError")}
        </p>
      ) : null}
      <div className="grid grid-cols-3 gap-2.5">
        {giftOptions.map((item) => {
          const on = drinkId === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setDrinkId(item.id)}
              aria-pressed={on}
              className={`flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 transition-all ${
                on
                  ? "border-[oklch(79%_0.12_80_/_50%)] bg-[oklch(79%_0.12_80_/_10%)]"
                  : "border-hairline bg-[oklch(100%_0_0_/_3%)]"
              }`}
            >
              <span className={on ? "text-gold" : "text-cream-2"}>
                <DrinkIcon name={item.iconName} size={28} />
              </span>
              <span className="line-clamp-1 text-center text-[0.7rem] text-cream-2">
                {item.nameTh}
              </span>
              <span className="font-semibold text-[0.7rem] text-gold">
                {formatBaht(item.priceMinor)}
              </span>
            </button>
          );
        })}
      </div>

      <h2 className="jod-kicker mt-6 mb-2.5">{t("send.attachMessage")}</h2>
      <div className="flex flex-wrap gap-2">
        {giftMessages.map((m) => (
          <Chip key={m} on={message === m} onClick={() => setMessage(m)}>
            {m}
          </Chip>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setContact((c) => !c)}
        className="glass mt-5 flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-left"
      >
        <span className={contact ? "text-lime" : "text-cream-3"}>
          <Icons.IconIG size={18} />
        </span>
        <span className="flex-1 text-cream-2 text-sm">
          {t("send.attachIg")}
          {!settings.allowContact ? (
            <span className="block text-gold text-xs">{t("send.contactOffWarning")}</span>
          ) : null}
        </span>
        <span
          className={`flex h-6 w-10 items-center rounded-full p-0.5 transition-colors ${
            contact ? "bg-lime" : "bg-[oklch(100%_0_0_/_12%)]"
          }`}
        >
          <span
            className="size-5 rounded-full bg-cream transition-transform"
            style={{ transform: contact ? "translateX(1rem)" : "translateX(0)" }}
          />
        </span>
      </button>

      {!guard.ok ? (
        <p className="mt-3 rounded-lg bg-red-500/10 px-3 py-2 text-red-100 text-xs">
          {guard.reason}
        </p>
      ) : null}

      <Button
        size="lg"
        variant="accent"
        className="mt-5 w-full"
        disabled={!drink || !guard.ok}
        onClick={() => setConfirmOpen(true)}
      >
        <Icons.IconCheers size={20} /> {t("send.sealAndSend")}
      </Button>

      <ConfirmSheet
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("send.confirmTitle")}
        description={t("send.confirmDesc", { item: drink?.name ?? "", table: target?.code ?? "" })}
        confirmLabel={t("send.confirmSeal")}
        cancelLabel={t("common.notYet")}
        onConfirm={send}
      />
    </div>
  );
}
